<!--
/**
 * StickyQuestionnaire Component
 * 
 * A two-question survey system that appears as a sticky element on articles.
 * This component implements a time-based progressive questionnaire flow.
 * 
 * =============================================================================
 * HOW IT WORKS:
 * =============================================================================
 * 
 * 1. CONDITIONAL RENDERING:
 *    - Only shows if post['acf']['enable_survey_popup'] is true
 *    - Requires articleId prop to function
 * 
 * 2. QUESTION FLOW:
 *    FIRST VISIT:
 *    - Shows Question 1 when user scrolls 70% of page
 *    - User selects answer and submits
 *    - Survey closes and sets cookie with answer + date
 *    - Sends GTM event for Question 1
 * 
 *    RETURN VISIT (within waiting period):
 *    - Survey stays closed (user already answered recently)
 * 
 *    RETURN VISIT (after waiting period):
 *    - Shows Question 2
 *    - User selects answer and submits  
 *    - Updates cookie with Question 2 data
 *    - Sends combined GTM event with both answers
 *    - Survey permanently closes for this article
 * 
 * 3. BACKWARD COMPATIBILITY:
 *    - Detects old cookie format (feedback_submitted_articleId)
 *    - Migrates to new JSON structure automatically
 *    - Preserves user's previous Question 1 answer
 *    - Sets migration date as answer date
 * 
 * 4. COOKIE STRUCTURE:
 *    Key: 'survey_data_' + articleId
 *    Value: JSON object:
 *    {
 *      "question1": { "answer": "Sehr", "date": "2025-08-13T..." },
 *      "question2": { "answer": "Sehr hilfreich", "date": "2025-08-20T..." }
 *    }
 * 
 * 5. GTM EVENTS:
 *    Question 1: 'feedback_submission' 
 *    Question 2: 'feedback_submission_question2' (includes both answers)
 * 
 * 6. CONFIGURATION:
 *    - daysUntilQuestion2: Days to wait before showing Question 2 (default: 1)
 *    - scrollThreshold: Scroll percentage to trigger survey (default: 70%)
 *    - Questions are defined in data.questions object
 * 
 * 7. COOKIE CONSENT:
 *    - Requires OneTrust C0002 (Performance) cookies to be accepted
 *    - Shows overlay when cookies not accepted
 *    - Polls for OneTrust availability on mount
 * 
 * =============================================================================
 * USAGE:
 * =============================================================================
 * <sticky-questionnaire :articleId="articleSlug" />
 * 
 * Make sure the parent component has:
 * v-if="post['acf']['enable_survey_popup']"
 */
-->

<template>
  <figure 
    class="sticky-questionnaire" 
    :class="{ 
      'expanded': isExpanded, 
      'submitted': isSubmitted,
      'sticky-questionnaire--closed': !isExpanded,
      'cookie-consent-rejected': !cookiesAccepted
    }"
  >
    <div 
      class="sticky-questionnaire__tab" 
      @click="toggleExpand()"
    >
      <span v-if="!isSubmitted" class="sticky-questionnaire__tab-text" v-html="currentQuestion.tabText"></span>
      <span v-if="isSubmitted" class="sticky-questionnaire__tab-text">Danke für deine Bewertung!</span>
    </div>

    <section class="sticky-questionnaire__content">
      <button 
        class="sticky-questionnaire__close" 
        @click="toggleExpand()"
      >×</button>
      
      <div 
        class="sticky-questionnaire__content-wrapper"
        @click="!cookiesAccepted && openCookieSettings()"
      >
        <div v-if="!isSubmitted" class="sticky-questionnaire__form">
          <h3 class="sticky-questionnaire__question" v-html="currentQuestion.question"></h3>
          
          <div class="sticky-questionnaire__options">
            <label 
              v-for="option in currentQuestion.options" 
              :key="option.value"
              class="sticky-questionnaire__option"
            >
              <input 
                type="radio" 
                :name="currentQuestion.name" 
                :value="option.value" 
                v-model="selectedOption"
                @change="handleOptionChange(option.value)"
                :disabled="!cookiesAccepted"
              />
              <span class="checkbox-custom"></span>
              {{ option.label }}
            </label>
          </div>
          
          <button 
            class="sticky-questionnaire__submit" 
            @click="cookiesAccepted ? submitFeedback() : openCookieSettings()"
            :disabled="!selectedOption && cookiesAccepted"
          >
            Absenden
          </button>
        </div>
        
        <!-- Thank you message -->
        <div v-else class="sticky-questionnaire__thank-you">
          <p>Danke für Deine Bewertung!</p>
        </div>

        <!-- Cookie consent overlay -->
        <div v-if="!cookiesAccepted" class="cookie-consent-overlay" @click.prevent="openCookieSettings">
          <p>
            Hinweis: Um an der Umfrage teilnehmen zu können, akzeptiere bitte hier die funktionellen Cookies:
          </p>
          <a href="#" @click.prevent="openCookieSettings">Cookie-Einstellungen verwalten.</a>
        </div>
      </div>
    </section>
  </figure>
</template>

<script>
export default {
  name: 'StickyQuestionnaire',
  props: {
    articleId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isExpanded: false,
      isSubmitted: false,
      selectedOption: null,
      automaticlyOpenOnScroll: true,
      cookieKey: 'survey_data_',
      cookiesAccepted: false,
      oneTrustLoaded: false,
      oneTrustPollingInterval: null,
      currentQuestionNumber: 1,
      daysUntilQuestion2: 30, // Days to wait before showing question 2
      scrollThreshold: 70, // Changed from 70% to 50% as per original requirement
      questions: {
        1: {
          name: 'empowerment',
          question: 'Hast du die Informationen, um in deinem Behandlungs&shy;gespräch über Resistenzen zu sprechen?',
          tabText: 'Hast du die Informationen, um in deinem Behandlungs&shy;gespräch über Resistenzen zu sprechen?',
          options: [
            { value: 'Ja_und_ich_habs_vor', label: 'Ja und ich hab’s vor' },
            { value: 'Ja_habe_es_schon_besprochen', label: 'Ja, habe es schon besprochen' },
            { value: 'Ich_brauche_mehr_Infos', label: 'Ich brauche mehr Infos' }
          ]
        },
        2: {
          name: 'follow_up',
          question: 'Hast du in deinem Behandlungs&shy;gespräch schon über Resistenzen gesprochen?',
          tabText: 'Hast du in deinem Behandlungs&shy;gespräch schon über Resistenzen gesprochen?',
          options: [
            { value: 'Ja_oefter', label: 'Ja, öfter' },
            { value: 'Ja_habe_ich_vor', label: 'Ja, habe ich vor' },
            { value: 'Ich_brauche_mehr_Infos', label: 'Ich brauche mehr Infos' }
          ]
        }
      }
    };
  },
  computed: {
    currentQuestion() {
      return this.questions[this.currentQuestionNumber];
    }
  },
  mounted() {
    this.initializeSurvey();
    window.addEventListener('scroll', this.handleScroll);
    this.startOneTrustPolling();
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
    this.removeOneTrustListener();
    if (this.oneTrustPollingInterval) {
      clearInterval(this.oneTrustPollingInterval);
    }
  },
  watch: {
    oneTrustLoaded(newValue) {
      if (newValue) {
        this.setupOneTrustListener();
        this.checkCookieConsent();
      }
    }
  },
  methods: {
    /**
     * Initializes the survey by determining which question to show
     */
    initializeSurvey() {
      const surveyData = this.getSurveyData();
      
      if (!surveyData) {
        // Check for old cookie format for backward compatibility
        const oldCookieValue = this.getCookie('feedback_submitted_' + this.articleId);
        if (oldCookieValue) {
          // Migrate old cookie to new format
          const migrationDate = new Date().toISOString();
          const migratedData = {
            question1: {
              answer: oldCookieValue,
              date: migrationDate
            }
          };
          this.setSurveyData(migratedData);
          
          // Check if enough time has passed for question 2 (assume old answer was today for migration)
          const daysPassed = 0; // Since we're migrating today
          
          if (daysPassed >= this.daysUntilQuestion2) {
            this.currentQuestionNumber = 2;
            this.isSubmitted = false;
          } else {
            this.isSubmitted = true;
          }
        } else {
          // No previous data at all, show question 1
          this.currentQuestionNumber = 1;
          this.isSubmitted = false;
        }
      } else {
        // Check if enough time has passed for question 2
        const daysPassed = this.getDaysSinceAnswer(surveyData.question1.date);
        
        // if // Already answered both questions
        if (surveyData.question2) {
          this.isSubmitted = true;
        } else if (daysPassed >= this.daysUntilQuestion2) {
          // Time to show question 2
          this.currentQuestionNumber = 2;
          this.isSubmitted = false;
        } else {
          // Still within waiting period for question 2
          this.isSubmitted = true;
        }
      }
    },
    
    /**
     * Gets survey data from cookie
     */
    getSurveyData() {
      const cookieValue = this.getCookie(this.cookieKey + this.articleId);
      if (cookieValue) {
        try {
          return JSON.parse(decodeURIComponent(cookieValue));
        } catch (e) {
          console.error('Error parsing survey data:', e);
          return null;
        }
      }
      return null;
    },
    
    /**
     * Sets survey data in cookie
     */
    setSurveyData(data) {
      try {
        const cookieValue = encodeURIComponent(JSON.stringify(data));
        this.setCookie(this.cookieKey + this.articleId, cookieValue, 999);
      } catch (e) {
        console.error('Error setting survey data:', e);
      }
    },
    
    /**
     * Calculates days since a given date
     */
    getDaysSinceAnswer(dateString) {
      const answerDate = new Date(dateString);
      const currentDate = new Date();
      const timeDiff = currentDate.getTime() - answerDate.getTime();
      return Math.floor(timeDiff / (1000 * 3600 * 24));
    },
    
    /**
     * Starts polling for the OneTrust script to be loaded.
     */
    startOneTrustPolling() {
      this.oneTrustPollingInterval = setInterval(() => {
        if (window.OneTrust && window.OneTrust.GetDomainData) {
          this.oneTrustLoaded = true;
          clearInterval(this.oneTrustPollingInterval);
        }
      }, 100);
    },
    
    /**
     * Toggles the expansion state of the questionnaire.
     */
    toggleExpand() {
      this.automaticlyOpenOnScroll = false;
      this.isExpanded = !this.isExpanded;
    },
    
    /**
     * Handles the scroll event to determine when to show the survey
     */
    handleScroll() {
      const scrollPosition = window.scrollY;
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercentage = (scrollPosition / documentHeight) * 100;
      
      if (scrollPercentage >= this.scrollThreshold && !this.isExpanded && !this.isSubmitted && this.cookiesAccepted && this.automaticlyOpenOnScroll) {
        this.isExpanded = true;
      }
    },
    
    /**
     * Handles the change of the selected option
     */
    handleOptionChange(option) {
      this.selectedOption = option;
    },
    
    /**
     * Submits the feedback for the current question
     */
    submitFeedback() {
      if (!this.selectedOption || !this.cookiesAccepted) return;
      
      const currentDate = new Date().toISOString();
      let surveyData = this.getSurveyData() || {};
      
      if (this.currentQuestionNumber === 1) {
        // Submit question 1
        surveyData.question1 = {
          answer: this.selectedOption,
          date: currentDate
        };
        
        // Send GTM event for question 1
        if (window.gtag) {
          window.dataLayer.push({
            event: 'feedback_submission',
            question_id: 'empowerment',
            answer_selected: this.selectedOption,
            article_id: this.articleId,
            article_url: window.location.href,
            question_number: 1
          });
        }
        
      } else if (this.currentQuestionNumber === 2) {
        // Submit question 2
        surveyData.question2 = {
          answer: this.selectedOption,
          date: currentDate
        };
        
        // Send combined GTM event for both questions
        if (window.gtag) {
          window.dataLayer.push({
            event: 'feedback_submission_question2',
            question1_answer: surveyData.question1.answer,
            question2_answer: this.selectedOption,
            article_id: this.articleId,
            article_url: window.location.href,
            question_number: 2
          });
        }
      }
      
      // Save updated survey data
      this.setSurveyData(surveyData);
      this.isSubmitted = true;
      
      setTimeout(() => {
        this.isExpanded = false;
      }, 2500);
    },
    
    /**
     * Sets a cookie with the specified name, value, and expiration days.
     */
    setCookie(name, value, days) {
      try {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        const expires = "; expires=" + date.toUTCString();
        document.cookie = name + "=" + value + expires + "; path=/";
      } catch (e) {
        console.error('Could not set cookie', e);
      }
    },
    
    /**
     * Retrieves the value of a specified cookie by its name.
     */
    getCookie(name) {
      const nameEQ = name + "=";
      const ca = document.cookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
      }
      return null;
    },
    
    /**
     * Checks if the user has given consent for performance cookies (C0002).
     */
    checkCookieConsent() {
      const activeGroups = OnetrustActiveGroups || "";
      this.cookiesAccepted = activeGroups.includes("C0002");
      return this.cookiesAccepted;
    },
    
    /**
     * Opens the cookie settings dialog using the OneTrust library.
     */
    openCookieSettings() {
      if (window.OneTrust) {
        window.OneTrust.ToggleInfoDisplay();
      }
    },
    
    /**
     * Sets up a listener for OneTrust consent changes.
     */
    setupOneTrustListener() {
      if (window.OneTrust) {
        window.OneTrust.OnConsentChanged(this.handleConsentChange);
      }
    },
    
    /**
     * Removes the OneTrust consent change listener if it exists.
     */
    removeOneTrustListener() {
      if (window.OneTrust) {
        // Note: OneTrust doesn't provide a remove handler method
        // This is left as a placeholder for future implementation
      }
    },
    
    /**
     * Handles the change in consent by checking the cookie consent status.
     */
    handleConsentChange() {
      this.checkCookieConsent();
    }
  }
}
</script>

<style scoped>
.sticky-questionnaire {
  position: fixed;
  right: 0;
  top: 62%;
  transform: translateY(-50%);
  z-index: 999;
  display: flex;
  flex-direction: row-reverse;
  transition: all 0.3s ease;
}
.sticky-questionnaire.submitted:not(.expanded) .sticky-questionnaire__tab,.sticky-questionnaire.submitted:not(.expanded) .sticky-questionnaire__content{
  background-color: #d1d1d1 !important;
}

.sticky-questionnaire__tab {
  background-color: #e73d50;
  color: white;
  padding: 15px;
  cursor: pointer;
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  height: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  border-radius: 0px 2px 2px 0px;
}
.sticky-questionnaire__tab-text{
  text-align: center;
}

.sticky-questionnaire__content {
  background-color: #e73d50;
  color: white;
  width: 0;
  height: 0;
  overflow: hidden;
  transition: width 0.3s ease, height 0.3s ease;
  position: relative;
  border-radius: 0 0 0 0;
}

.expanded .sticky-questionnaire__content {
  width: 330px;
  height: auto;
  padding: 0px;
}

.expanded .sticky-questionnaire__tab {
  display: none;
}

.sticky-questionnaire__close {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  color: white;
  font-size: 35px;
  cursor: pointer;
  padding: 0;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sticky-questionnaire__close{
  z-index: 1000;
}
.cookie-consent-rejected .sticky-questionnaire__close{
  top: -2px;
}

.sticky-questionnaire__question {
  font-size: 18px;
  margin-bottom: 20px;
  font-weight: bold;
  text-align: left;
}

.sticky-questionnaire__options {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.sticky-questionnaire__option {
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  position: relative;
  padding: 10px;
  flex: 1;
  text-align: left; /* left align like in reference */
  white-space: nowrap; /* keep answers on one line */
  overflow: hidden; /* prevent overflow from breaking layout */
  text-overflow: ellipsis; /* show ellipsis if too long */
}

.sticky-questionnaire__option input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkbox-custom {
  position: relative;
  height: 24px;
  width: 24px;
  background-color: transparent;
  border: 1px solid white;
  margin-right: 7px;
}

.sticky-questionnaire__option input:checked ~ .checkbox-custom {
  background-color: transparent;
}

.checkbox-custom:after {
  content: "";
  position: absolute;
  display: none;
}

.sticky-questionnaire__option input:checked ~ .checkbox-custom:after {
  display: block;
}

.sticky-questionnaire__option .checkbox-custom:after {
  left: 50%;
  top: 44%;
  width: 16px;
  height: 16px;
  border: none;
  transform: translate(-50%, -50%);
  content: "×";
  color: white;
  font-size: 20px;
  line-height: 16px;
  text-align: center;
}

.sticky-questionnaire__submit {
  background-color: #000;
  color: white;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  font-weight: bold;
  font-size: 15px;
  line-height: 21px;
  transition: background-color 0.3s ease, transform 0.2s ease;
}

.sticky-questionnaire__submit:disabled {
  cursor: not-allowed;
}

.sticky-questionnaire__thank-you {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 186px;
  font-weight: bold;
  font-size: 18px;
  text-align: center;
}
.sticky-questionnaire__thank-you p{
  margin-bottom: 0.5rem;
}


.cookie-consent-rejected.expanded .sticky-questionnaire__content{
  border: 1px solid #000;
}
.cookie-consent-rejected .sticky-questionnaire__content::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1;
}

.cookie-consent-rejected .cookie-consent-overlay {
  z-index: 2;
}


.cookie-consent-rejected .sticky-questionnaire__content-wrapper {
  cursor: pointer;
}

.cookie-consent-rejected .sticky-questionnaire__form,
.cookie-consent-rejected .sticky-questionnaire__thank-you {
  opacity: 0.5;
  pointer-events: none;
  padding: 50px 12px 18.5px; /* extra space to the right for the X */
}
.sticky-questionnaire__form{
  padding: 50px 12px 18.5px;
}

@media (max-width: 768px) {
  .sticky-questionnaire__form {
    padding: 20px;
    padding-top: 50px;
    padding-right: 48px;
  }
  
  .cookie-consent-rejected .sticky-questionnaire__form {
    padding: 20px;
    padding-top: 50px;
    padding-right: 48px;
  }
}

.cookie-consent-overlay {
  position: relative;
  background-color: white;
  color: black;
  padding: 12.5px;
  font-size: 11px;
  text-align: left;
  z-index: 2;
}

.cookie-consent-overlay p {
  margin: 0 0 5px 0;
}

.cookie-consent-overlay a {
  color: black;
  text-decoration: underline;
  cursor: pointer;
}

@media (max-width: 768px) {
  .sticky-questionnaire {
    position: fixed;
    right: auto;
    left: 50%;
    top: auto;
    bottom: 0;
    transform: translateX(-50%);
    flex-direction: column;
    width: 100%;

    height: auto;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: stretch;
    justify-items: center;
    align-content: space-between;
    flex-wrap: nowrap;
    margin-bottom: 0px;

  }
  
  .sticky-questionnaire__tab {
    writing-mode: initial;
    transform: none;
    height: auto;
    width: 100%;
    padding: 15px;
    text-align: center;
    border-radius: 0px 0px 0 0;
    
  }
  
  .sticky-questionnaire__tab-text {
    text-align: center;
  }
  
  .expanded .sticky-questionnaire__content {
    width: 100%;
    height: auto;
    padding: 0;
    border-radius: 8px 8px 0 0;
  }
  
  .expanded .sticky-questionnaire__tab {
    display: none;
  }
  
  .sticky-questionnaire__content {
    border-radius: 8px 8px 0 0;
  }
  
  .sticky-questionnaire__question {
    font-size: 16px;
  }
  
  .sticky-questionnaire__options {
    flex-direction: column;
    gap: 10px;
  }
  
  .sticky-questionnaire__option {
    justify-content: flex-start;
    text-align: left;
    padding: 15px;
  }
  .sticky-questionnaire--closed{
    height: 35px !important;
  }
}

.sticky-questionnaire__option:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

.sticky-questionnaire__submit:hover:not(:disabled) {
  background-color: #333;
  transform: translateY(-2px);
}

.sticky-questionnaire__submit:active:not(:disabled) {
  transform: translateY(0);
}
</style>